import { useState, useEffect } from "react";
import {
  Card,
  Button,
  Select,
  SelectItem,
  DatePicker,
  Spinner,
  Tabs,
  Tab,
} from "@heroui/react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowLeft,
  Download,
  TrendingUp,
  DollarSign,
  ShoppingBag,
  CreditCard,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { reportsService } from "@/services/reportsService";
import { formatCurrency, formatDate } from "@/utils/formatters";
import { parseDate } from "@internationalized/date";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

export default function SalesReportPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [groupBy, setGroupBy] = useState("day");
  const [orderType, setOrderType] = useState<string | null>(null);
  const [statusId, setStatusId] = useState<number | null>(null);
  const [salesData, setSalesData] = useState<any>(null);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchSalesReport();
  }, [startDate, endDate, groupBy, orderType, statusId]);

  const fetchInitialData = async () => {
    try {
      const configResponse = await fetch("/v1/api/config/order-statuses");

      if (!configResponse.ok) {
        throw new Error("Error loading order statuses");
      }
      const statusesData = await configResponse.json();

      setStatuses(statusesData);
    } catch (err) {
      console.error("Error loading initial data:", err);
      setError("Error loading order statuses");
    }
  };

  const fetchSalesReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: startDate,
        end_date: endDate,
        group_by: groupBy as "day" | "week" | "month",
        status_id: statusId || undefined,
        order_type: orderType || undefined,
      };

      const data = await reportsService.getSalesReport(params);

      setSalesData(data);
    } catch (err) {
      console.error("Error loading sales report:", err);
      setError("Error loading sales report");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/reports");
  };

  const handleExportCSV = () => {
    alert("CSV export will be implemented here");
  };

  const prepareTimeSeriesData = () => {
    if (!salesData) return [];

    if (groupBy === "day") {
      return (
        salesData.daily_sales?.map((day: any) => ({
          ...day,
          date: formatDate(day.date),
        })) || []
      );
    } else if (groupBy === "week") {
      return salesData.weekly_sales?.map((week: any) => ({
        ...week,
        date: `Week ${week.week}`,
      })) || [];
    } else {
      return (
        salesData.monthly_sales?.map((month: any) => ({
          ...month,
          date: month.month,
        })) || []
      );
    }
  };

  const preparePieData = (dataObj: any) => {
    if (!dataObj) return [];

    return Object.entries(dataObj).map(([key, value]) => ({
      name: key,
      value: value as number,
    }));
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Button isIconOnly variant="light" onPress={handleBack}>
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-2xl font-bold">Sales Report</h1>
        </div>
        <div className="flex gap-2">
          <Button
            color="primary"
            startContent={<Download size={16} />}
            variant="flat"
            onPress={handleExportCSV}
          >
            Export CSV
          </Button>
        </div>
      </div>

      <Card className="p-4">
        {error && <div className="text-danger mb-4">{error}</div>}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <DatePicker
            label="Start Date"
            value={parseDate(startDate)}
            onChange={(date) => setStartDate(date?.toString() || "")}
          />
          <DatePicker
            label="End Date"
            value={parseDate(endDate)}
            onChange={(date) => setEndDate(date?.toString() || "")}
          />
          <Select
            label="Group By"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <SelectItem key="day" textValue="Day">
              Day
            </SelectItem>
            <SelectItem key="week" textValue="Week">
              Week
            </SelectItem>
            <SelectItem key="month" textValue="Month">
              Month
            </SelectItem>
          </Select>
          <Select
            label="Order Type"
            placeholder="All Types"
            value={orderType || ""}
            onChange={(e) => setOrderType(e.target.value || null)}
          >
            <SelectItem key="all" textValue="All Types">
              All Types
            </SelectItem>
            <SelectItem key="pickup" textValue="Pickup">
              Pickup
            </SelectItem>
            <SelectItem key="delivery" textValue="Delivery">
              Delivery
            </SelectItem>
          </Select>
          <Select
            label="Status"
            placeholder="All Statuses"
            value={statusId?.toString() || ""}
            onChange={(e) =>
              setStatusId(e.target.value ? parseInt(e.target.value) : null)
            }
          >
            <SelectItem key="all" textValue="All Statuses">
              All Statuses
            </SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status.id} textValue={status.name}>
                {status.name}
              </SelectItem>
            )) as any}
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center p-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-default-500">Total Sales</p>
                    <p className="text-2xl font-semibold">
                      {salesData?.summary?.total_sales
                        ? formatCurrency(salesData.summary.total_sales)
                        : "$0.00"}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-primary" />
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-default-500">Total Orders</p>
                    <p className="text-2xl font-semibold">
                      {salesData?.summary?.order_count ?? 0}
                    </p>
                  </div>
                  <ShoppingBag className="w-8 h-8 text-primary" />
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-default-500">Average Ticket</p>
                    <p className="text-2xl font-semibold">
                      {salesData?.summary?.average_order_value
                        ? formatCurrency(salesData.summary.average_order_value)
                        : "$0.00"}
                    </p>
                  </div>
                  <CreditCard className="w-8 h-8 text-primary" />
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-default-500">
                      Compared to Previous Period
                    </p>
                    <p className="text-2xl font-semibold">
                      {salesData?.summary?.compared_to_previous !==
                      undefined ? (
                        <span
                          className={
                            salesData.summary.compared_to_previous >= 0
                              ? "text-success"
                              : "text-danger"
                          }
                        >
                          {salesData.summary.compared_to_previous >= 0
                            ? "+"
                            : ""}
                          {salesData.summary.compared_to_previous.toFixed(2)}%
                        </span>
                      ) : (
                        "N/A"
                      )}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-primary" />
                </div>
              </Card>
            </div>

            <Tabs>
              <Tab key="time-series" title="Sales Over Time">
                <div className="h-96 mt-4">
                  <ResponsiveContainer height="100%" width="100%">
                    <LineChart
                      data={prepareTimeSeriesData()}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 10,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis
                        orientation="left"
                        stroke="#8884d8"
                        yAxisId="left"
                      />
                      <YAxis
                        orientation="right"
                        stroke="#82ca9d"
                        yAxisId="right"
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                      />
                      <Legend />
                      <Line
                        activeDot={{ r: 8 }}
                        dataKey="total_sales"
                        name="Sales ($)"
                        stroke="#8884d8"
                        type="monotone"
                        yAxisId="left"
                      />
                      <Line
                        dataKey="order_count"
                        name="Orders"
                        stroke="#82ca9d"
                        type="monotone"
                        yAxisId="right"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Tab>

              <Tab key="payment" title="By Payment Method">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="h-96">
                    <ResponsiveContainer height="100%" width="100%">
                      <PieChart>
                        <Pie
                          cx="50%"
                          cy="50%"
                          data={preparePieData(
                            salesData?.sales_by_payment_method,
                          )}
                          dataKey="value"
                          fill="#8884d8"
                          label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                          }
                          outerRadius={120}
                        >
                          {preparePieData(
                            salesData?.sales_by_payment_method,
                          ).map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => formatCurrency(value as number)}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="h-96">
                    <ResponsiveContainer height="100%" width="100%">
                      <BarChart
                        data={preparePieData(
                          salesData?.sales_by_payment_method,
                        )}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip
                          formatter={(value) => formatCurrency(value as number)}
                        />
                        <Legend />
                        <Bar dataKey="value" name="Sales" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Tab>

              <Tab key="order-type" title="By Order Type">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="h-96">
                    <ResponsiveContainer height="100%" width="100%">
                      <PieChart>
                        <Pie
                          cx="50%"
                          cy="50%"
                          data={preparePieData(salesData?.sales_by_order_type)}
                          dataKey="value"
                          fill="#8884d8"
                          label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                          }
                          outerRadius={120}
                        >
                          {preparePieData(salesData?.sales_by_order_type).map(
                            (_, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ),
                          )}
                        </Pie>
                        <Tooltip
                          formatter={(value) => formatCurrency(value as number)}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="h-96">
                    <ResponsiveContainer height="100%" width="100%">
                      <BarChart
                        data={preparePieData(salesData?.sales_by_order_type)}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip
                          formatter={(value) => formatCurrency(value as number)}
                        />
                        <Legend />
                        <Bar dataKey="value" name="Sales" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Tab>

              <Tab key="hour" title="By Hour of Day">
                <div className="h-96 mt-4">
                  <ResponsiveContainer height="100%" width="100%">
                    <BarChart
                      data={preparePieData(salesData?.average_order_by_hour)}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                      />
                      <Legend />
                      <Bar
                        dataKey="value"
                        name="Average Sales"
                        fill="#8884d8"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Tab>
            </Tabs>
          </div>
        )}
      </Card>
    </div>
  );
}
